import { useEffect, useRef, useState, type ReactNode } from "react";
import { mindsetAccountExperienceAdapter as adapter } from "./accountExperienceAdapter";
import { consumeAuthReturn } from "./authReturn";
import type { MindsetAccountAuthConfig, MindsetEmailChallenge, MindsetEmailAuthResult } from "@mindset/sdk";
import "./emailCodeAccess.css";

export async function authOperation<T>(operation: string, body?: Record<string, unknown>) {
  if (!adapter.accountAuth) throw new Error("Secure sign-in is unavailable.");
  return adapter.accountAuth<T>(operation, body);
}
export function validChallenge(value: MindsetEmailChallenge) {
  return value && typeof value.challengeToken === "string" && value.challengeToken.length > 0 && value.challengeToken.length <= 4096 && Number.isFinite(Date.parse(value.expiresAt)) && Date.parse(value.expiresAt) > Date.now() && Number.isSafeInteger(value.resendAfterSeconds) && value.resendAfterSeconds >= 0 && value.resendAfterSeconds <= 3600;
}
export async function completeEmailResult(value: MindsetEmailAuthResult) {
  if (value.kind !== "SIGNED_IN" || !adapter.completeEmailAuthentication) throw new Error("Authentication is incomplete.");
  await adapter.completeEmailAuthentication(value.customToken);
}
export function formatCodeExpiry(seconds: number) {
  const remaining = Math.max(0, Math.ceil(seconds));
  const minutes = Math.floor(remaining / 60);
  const rest = remaining % 60;
  return [minutes ? `${minutes} minute${minutes === 1 ? "" : "s"}` : "",
    rest || !minutes ? `${rest} second${rest === 1 ? "" : "s"}` : ""].filter(Boolean).join(" ");
}
export function EmailAccessGate({ children, onAuthenticated }: { children: ReactNode; onAuthenticated?: () => Promise<void> }) {
  const [config, setConfig] = useState<MindsetAccountAuthConfig | null>(null);
  const [error, setError] = useState(false); const [retry, setRetry] = useState(0);
  useEffect(() => {
    let current = true; setError(false); setConfig(null);
    if (!adapter.accountAuth) { setConfig({ emailEnabled: false, authenticatorEnabled: false }); return; }
    void authOperation<MindsetAccountAuthConfig>("config").then(value => {
      if (typeof value.emailEnabled !== "boolean" || typeof value.authenticatorEnabled !== "boolean") throw Error();
      if (current) setConfig(value);
    }).catch(reason => { if (current) {
      if (reason?.status === 404) setConfig({ emailEnabled: false, authenticatorEnabled: false });
      else setError(true);
    } });
    return () => { current = false; };
  }, [retry]);
  if (error) return <div role="alert">Secure sign-in is unavailable.<button type="button" onClick={() => setRetry(n => n + 1)}>Try again</button></div>;
  if (!config) return <p role="status">Checking secure sign-in...</p>;
  return config.emailEnabled ? <EmailCodeAccess onAuthenticated={onAuthenticated} /> : <>{children}</>;
}
export function EmailCodeAccess({ onAuthenticated }: { onAuthenticated?: () => Promise<void> }) {
  const [email,setEmail]=useState(""); const [code,setCode]=useState("");
  const [challenge,setChallenge]=useState<MindsetEmailChallenge | null>(null);
  const [mfa,setMfa]=useState(false); const [recovery,setRecovery]=useState(false);
  const [error,setError]=useState(""); const [busy,setBusy]=useState(false); const [now,setNow]=useState(Date.now());
  const live=useRef(true); const pending=useRef(false);
  useEffect(() => { live.current=true; const timer=setInterval(()=>setNow(Date.now()),1000); return ()=>{live.current=false;clearInterval(timer);}; },[]);
  const expired=Boolean(challenge && Date.parse(challenge.expiresAt)<=now);
  const resendAt=useRef(0);
  async function submit() {
    if(pending.current)return; pending.current=true;setBusy(true);setError("");
    try {
      if(!challenge){
        const next=await authOperation<MindsetEmailChallenge>("email/request",{email:email.trim()});
        if(!validChallenge(next))throw Error();
        if(live.current){setChallenge(next);resendAt.current=Date.now()+next.resendAfterSeconds*1000;setCode("");}
      } else {
        const result=await authOperation<MindsetEmailAuthResult>(mfa?"mfa/verify":"email/verify",{challengeToken:challenge.challengeToken,code,...(mfa?{method:recovery?"RECOVERY":"TOTP"}:{})});
        if(!live.current)return;
        if(result.kind==="MFA_REQUIRED"){
          const next={...result,resendAfterSeconds:0}; if(!validChallenge(next))throw Error();
          setChallenge(next);setMfa(true);setCode("");
        } else {
          await completeEmailResult(result); if(!live.current)return;
          if(onAuthenticated)await onAuthenticated(); else window.location.assign(consumeAuthReturn());
        }
      }
    } catch {if(live.current)setError("Unable to verify. Check your code or try again shortly.");}
    finally{pending.current=false;if(live.current)setBusy(false);}
  }
  return <form className="email-code-access" onSubmit={event=>{event.preventDefault();void submit();}}>
    {!challenge ? <label>Email<input type="email" autoComplete="email" required value={email} onChange={e=>setEmail(e.target.value)} disabled={busy}/></label>
      : <><p>{mfa?"Enter your authenticator or recovery code.":"If eligible, a sign-in code has been sent to your email."}</p>
        <label>{recovery?"Recovery code":mfa?"Authenticator code":"Email code"}<input required value={code} disabled={busy||expired} autoComplete="one-time-code" inputMode={recovery?"text":"numeric"} autoCapitalize="none" spellCheck={false} pattern={recovery?"[A-Za-z0-9_\\-]{22}":"[0-9]{6}"} maxLength={recovery?22:6} onChange={e=>setCode(e.target.value)}/></label>
        <p role="status">{expired?"Code expired. Request a new email code.":`Expires in ${formatCodeExpiry((Date.parse(challenge.expiresAt)-now)/1000)}`}</p>
        {mfa&&<label><input type="checkbox" checked={recovery} onChange={e=>{setRecovery(e.target.checked);setCode("");}}/>Use a recovery code</label>}
      </>}
    {error&&<p role="alert">{error}</p>}
    <button type="submit" disabled={busy||expired}>{busy?"Checking...":challenge?"Verify and continue":"Send sign-in code"}</button>
    {challenge&&<button type="button" disabled={busy||now<resendAt.current} onClick={()=>{setChallenge(null);setMfa(false);setRecovery(false);setCode("");setError("");}}>Use email again{now<resendAt.current?` (${Math.ceil((resendAt.current-now)/1000)}s)`:""}</button>}
  </form>;
}
